import React, { useState } from 'react';
import { API_URL, apiFetch } from '../services/api';

interface ContactFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedPackage?: string;
}

export const ContactFormModal: React.FC<ContactFormModalProps> = ({ isOpen, onClose, selectedPackage }) => {
    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        grade: selectedPackage || '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await apiFetch(`${API_URL}/api/contact-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setSuccess(true);
                setTimeout(() => {
                    onClose();
                    setSuccess(false);
                    setFormData({ name: '', phoneNumber: '', grade: '', message: '' });
                }, 3000);
            } else {
                const data = await response.json();
                setError(data.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
            }
        } catch (err) {
            setError('Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="p-8 pb-4 flex items-center justify-between border-b border-slate-50">
                    <div>
                        <h3 className="text-2xl font-black text-slate-900">Sizi Arayalım</h3>
                        <p className="text-slate-500 text-sm font-medium mt-1">Eğitim danışmanlarımız en kısa sürede size ulaşacaktır.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900 transition-all font-bold"
                        title="Kapat"
                    >
                        ✕
                    </button>
                </div>

                {success ? (
                    <div className="p-12 text-center animate-in zoom-in-95 duration-500">
                        <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                            ✓
                        </div>
                        <h4 className="text-2xl font-black text-slate-900 mb-2">Başarıyla Alındı!</h4>
                        <p className="text-slate-600 font-medium">Talebiniz bize ulaştı. Sizinle en kısa sürede iletişime geçeceğiz.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-8 space-y-5">
                        {error && (
                            <div className="bg-rose-50 border border-rose-100 text-rose-600 px-4 py-3 rounded-2xl text-sm font-bold animate-in slide-in-from-top-2">
                                {error}
                            </div>
                        )}

                        <div>
                            <label className="block text-slate-700 text-sm font-bold mb-2 ml-1">Ad Soyad *</label>
                            <input
                                required
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Örn: Ahmet Yılmaz"
                                className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                            />
                        </div>

                        <div>
                            <label className="block text-slate-700 text-sm font-bold mb-2 ml-1">Telefon Numarası *</label>
                            <input
                                required
                                type="tel"
                                value={formData.phoneNumber}
                                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                placeholder="05XX XXX XX XX"
                                className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                            />
                        </div>

                        <div>
                            <label className="block text-slate-700 text-sm font-bold mb-2 ml-1">İlgilenilen Alan / Sınıf *</label>
                            <select
                                required
                                value={formData.grade}
                                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                                className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium appearance-none cursor-pointer"
                            >
                                <option value="">Bir seçim yapın</option>
                                <option value="LGS Hazırlık (5-8. Sınıf)">LGS Hazırlık (5-8. Sınıf)</option>
                                <option value="YKS Hazırlık (Lise & Mezun)">YKS Hazırlık (Lise & Mezun)</option>
                                <option value="Eğitim Danışmanlığı">Eğitim Danışmanlığı</option>
                                <option value="Özel Ders + Danışmanlık">Özel Ders + Danışmanlık</option>
                                <option value="Diğer">Diğer</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-slate-700 text-sm font-bold mb-2 ml-1">Ek Not (Opsiyonel)</label>
                            <textarea
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                placeholder="Bize iletmek istediğiniz özel bir notunuz var mı?"
                                rows={3}
                                className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium resize-none"
                            />
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl shadow-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 active:scale-[0.98]"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        <span>Gönderiliyor...</span>
                                    </>
                                ) : (
                                    'Talep Gönder'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
